'use client';

import { Button } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { colors } from '@/styles/theme';

interface GoogleAuthButtonProps {
  onClick: () => void;
  disabled?: boolean;
  label?: string;
}

const GoogleLogo = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill={colors.brand.google.blue}
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill={colors.brand.google.green}
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill={colors.brand.google.yellow}
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill={colors.brand.google.red}
    />
  </svg>
);

export const GoogleAuthButton = ({
  onClick,
  disabled = false,
  label = 'Continue with Google',
}: GoogleAuthButtonProps) => (
  <Button
    fullWidth
    variant="contained"
    disableElevation
    startIcon={<GoogleLogo size={20} />}
    onClick={onClick}
    disabled={disabled}
    sx={(theme) => ({
      py: 1.25,
      borderRadius: 2.5,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
      mb: 1,
      color: theme.palette.grey[800],
      bgcolor: theme.palette.common.white,
      border: '1px solid',
      borderColor: theme.palette.grey[300],
      boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.3 : 0.08)}`,
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
      transform: 'none',
      ...(theme.palette.mode === 'dark'
        ? {
            bgcolor: colors.common.black,
            color: colors.common.white,
            borderColor: 'divider',
            '&:hover': {
              bgcolor: colors.common.blackHover,
              borderColor: colors.common.black,
            },
          }
        : {
            bgcolor: theme.palette.common.white,
            color: theme.palette.common.black,
            borderColor: theme.palette.grey[300],
            '&:hover': {
              bgcolor: theme.palette.grey[100],
              borderColor: theme.palette.grey[400],
            },
          }),
      '&.Mui-disabled': {
        bgcolor: 'action.disabledBackground',
        color: 'action.disabled',
      },
    })}
  >
    {label}
  </Button>
);
